import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Popper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { DateRangePickerDay as MuiDateRangePickerDay } from '@mui/x-date-pickers-pro/DateRangePickerDay';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import ArrowDownIcon from 'common/shared-ui/src/icons/ArrowDownIcon';

import {
  getFilterBasePlans,
  getFilterCalculationTypes,
  getFilterIssuePriorities,
  getFilterIssueRoles,
  getFilterIssueStatuses,
  getFilterIssueUserGroups,
  getFilterIssueWatcher,
  getFilterListItems,
  getFilterProjectParents,
  getFilterProjectStatuses,
  getFilterSubItems,
  getGantParentIssue,
  getIssueQuery,
  getParentIssueQuery,
  getQueryActivity,
  getQueryConfirmationApprovers,
  getQueryConfirmationAuthors,
  getQueryConfirmationConfirmations,
  getQueryConfirmationEditors,
  getQueryConfirmationIssues,
  getQueryConfirmationProjects,
  getQueryConfirmationStatuses,
  getQueryConfirmationTrackers,
  getQueryIssues,
  getQueryIssuesParams,
  getQueryIssueTrackers,
  getQueryProjects,
  getQueryTrackers,
  getQueryUser,
  getQueryUsers,
} from '../../../packages/common/api';
import CalendarFormIcon from '../../../packages/common/shared-ui/src/icons/CalendarFormIcon';
import CheckboxIcon from '../../../packages/common/shared-ui/src/icons/CheckboxIcon';
import CheckboxIconChecked from '../../../packages/common/shared-ui/src/icons/CheckboxIconChecked';
import ChevronIcon from '../../../packages/common/shared-ui/src/icons/ChevronIcon';
import ClearIcon from '../../../packages/common/shared-ui/src/icons/ClearIcon';
import { sortOptions } from '../../../packages/common/utils/fieldHelper';
import { useTranslation } from '../../../packages/common/utils/getLocalString';
import {
  filterDateValues,
  filtersDateRange,
  renderOptions,
} from '../../../packages/common/utils/gridFiltersHelperNew';
import { LanguageMap } from '../../../packages/common/utils/languageMap';
import { store } from '../app/redux';

// Original class: filterPopoverButton
const StyledFilterPopoverButton = styled(Button, {
  shouldForwardProp: prop => !['activeStyle'].includes(prop),
})(({ theme, activeStyle }) => ({
  '&.MuiButtonBase-root': {
    minWidth: '168px',
    justifyContent: 'space-between',
    border: '1px solid transparent',
    backgroundColor: '#fff',
    boxShadow: 'none',
    height: 'auto',
    padding: '7px 12px',
    minHeight: '40px',
    '&.MuiButton-root': {
      '&:focus': {
        backgroundColor: '#fff',
      },
    },
    '&:hover': {
      backgroundColor: '#fff',
      boxShadow: 'none',
    },
    '&.Mui-focused': {
      backgroundColor: '#fff',
      boxShadow: 'none',
    },
    ...(activeStyle && {
      borderColor: '#3448FF',
    }),
  },
}));
// Original class: filterPopover
const StyledFilterPopover = styled(Popover)(theme => ({
  '& .MuiPaper-root': {
    borderRadius: '16px',
    width: '288px',
    boxShadow: 'none',
    border: '1px solid #E4E4EF',
    overflow: 'hidden',
  },
}));
// Original class: selectLabel
const StyledSelectLabel = styled(InputLabel)(theme => ({
  '&.MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '10px',
    lineHeight: '20px',
    transform: 'none',
    left: '16px',
    top: '2px',
    '&.Mui-focused': {
      transform: 'none',
      left: '16px',
      top: '2px',
    },
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },
}));
// Original class: filterPopoverSelect
const StyledFilterPopoverSelect = styled(Select)(theme => ({
  minWidth: '130px',

  '&.MuiInputBase-root.MuiOutlinedInput-root': {
    height: '24px',
    '& .MuiInputBase-input.MuiOutlinedInput-input': {
      padding: '2px 0 2px 16px',
    },
    '& .MuiSvgIcon-root': {
      width: '24px',
      right: '8px',
    },
    '& .MuiSelect-nativeInput': {
      height: '100%',
    },
    '&.Mui-focused': {
      borderRadius: '16px 16px 0 0',
    },
  },
}));
// Original class: filterPopoverSelectMenu
const StyledFilterPopoverSelectMenu = styled(Paper)(theme => ({
  position: 'absolute',
  overflowY: 'auto',
  overflowX: 'hidden',
  borderRadius: '0 0 16px 16px',
  boxShadow: '0px 5px 10px -8px #A0A1AA66',
  border: '1px solid #E4E4EF',
  borderTop: 'none',
  '& ul': {
    padding: 0,
    '& li': {
      padding: '2px 16px',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
      '&.Mui-selected': {
        backgroundColor: '#DCDFF4!important',
        '&:hover': {
          backgroundColor: '#DCDFF4!important',
        },
        '&:focus': {
          backgroundColor: '#DCDFF4!important',
        },
      },
      '& .MuiTypography-root': {
        fontSize: '10px',
      },
    },
  },
}));
// Original class: filterPopoverTextfield
const StyledFilterPopoverTextfield = styled(TextField)(theme => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    height: 'auto',
    '&.MuiInputBase-multiline': {
      padding: 0,
    },
    '& .MuiInputBase-input.MuiOutlinedInput-input': {
      borderRadius: 0,
      fontSize: '12px',
      lineHeight: '20px',
      padding: '8px 16px',
      height: 'auto',
      color: '#212346',
      '&::placeholder': {
        color: '#7174AC',
        fontSize: '12px',
        lineHeight: '20px',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      boxShadow: 'none',
      border: 'none',
    },
  },
}));
// Original class: filterPopoverAutocomplete
const StyledFilterPopoverAutocomplete = styled(Autocomplete)(theme => ({
  width: '100%',

  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },

  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    padding: '8px 16px',
    paddingRight: '16px',
    lineHeight: '20px',
    height: 'auto',
    '& .MuiChip-root': {
      display: 'none',
    },
    '& .MuiAutocomplete-input': {
      fontSize: '12px',
      lineHeight: '20px',
      color: '#7174AC',
      padding: 0,
      height: 'auto',
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
  },

  '& + .MuiAutocomplete-popper': {
    position: 'static!important',
    transform: 'none!important',
    minWidth: 'auto!important',
    //width: '100%!important',
    maxWidth: '300px!important',
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0',
      border: 'none',
      boxShadow: 'none',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          padding: '8px 16px',
          '& .MuiButtonBase-root.MuiCheckbox-root': {
            padding: '2px',
            marginRight: '10px',
          },
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));
// Original class: rangeDatePicker
const StyledRangeDatePicker = styled(DateRangePicker)(theme => ({
  '& .MuiFormControl-root.MuiTextField-root': {
    '& .MuiFormLabel-root': {
      display: 'none',
    },
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      height: 'auto',
      padding: 0,
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
      '& .MuiInputAdornment-root': {
        '& .MuiSvgIcon-root path': {
          fill: '#DCDFF4',
        },
      },
    },
    '& .MuiInputBase-input.MuiOutlinedInput-input': {
      height: 'auto',
      padding: '8px 30px 8px 16px',
    },
  },

  '& .MuiInputAdornment-root': {
    position: 'absolute',
    right: '20px',
  },
}));
const DateRangePickerDay = styled(MuiDateRangePickerDay)(({ theme }) => ({
  variants: [
    {
      props: ({ isHighlighting, outsideCurrentMonth }) =>
        !outsideCurrentMonth && isHighlighting,
      style: {
        borderRadius: 0,
        backgroundColor: '#DCDFF4',
        color: 'rgba(0, 0, 0, 0.87)',
        '&:hover, &:focus': {
          backgroundColor: theme.palette.primary.dark,
        },
        '& .MuiButtonBase-root': {
          backgroundColor: 'transparent',
        },
      },
    },
    {
      props: ({ isStartOfHighlighting }) => isStartOfHighlighting,
      style: {
        //borderRadius: 0,
        borderTopLeftRadius: '50%',
        borderBottomLeftRadius: '50%',
        '& .MuiButtonBase-root': {
          backgroundColor: '#fff',
        },
      },
    },
    {
      props: ({ isEndOfHighlighting }) => isEndOfHighlighting,
      style: {
        //borderRadius: 0,
        borderTopRightRadius: '50%',
        borderBottomRightRadius: '50%',
        '& .MuiButtonBase-root': {
          backgroundColor: '#fff',
        },
      },
    },
    {
      props: ({ isStartOfPreviewing }) => isStartOfPreviewing,
      style: {
        '& .MuiButtonBase-root': {
          backgroundColor: 'rgb(178, 178, 178)',
        },
      },
    },
  ],
}));
// Original class: datePicker
const StyledDatePicker = styled(DatePicker)(theme => ({
  '&.MuiFormControl-root.MuiTextField-root': {
    //height: 'auto',
    //width: '116px',
    '& .MuiFormLabel-root': {
      display: 'none',
    },
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      height: 'auto',
      padding: 0,
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
      '& .MuiInputAdornment-root': {
        '& .MuiSvgIcon-root path': {
          fill: '#DCDFF4',
        },
      },
    },
    '& .MuiInputBase-input.MuiOutlinedInput-input': {
      height: 'auto',
      padding: '8px 30px 8px 16px',
    },
  },

  '& .MuiInputAdornment-root': {
    position: 'absolute',
    right: '20px',
  },
}));
// Original class: booleanLabel
const StyledBooleanLabel = styled(FormControlLabel)(theme => ({
  '&.MuiFormControlLabel-root': {
    margin: 0,
  },
}));
const GridFiltersPanelNew = ({ data, handleCloseFilter }) => {
  const getLocalString = useTranslation();
  const pageParams = useParams();
  const [filters, setFilters] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openedFilter, setOpenedFilter] = useState(null);
  const filtersTypeBoolean = useRef(['boolean', 'bool']);
  const filtersNumbers = useRef(['confirmation']);
  const filtersTypeText = useRef(['text', 'string']);
  const filtersTypeNumber = useRef(['float', 'int']);
  const disableFilterOperations = useRef(['all', 'null']);
  const filtersTypeAutocomplete = useRef([
    'project',
    'activity',
    'calculation_types',
    'user_groups',
    'issue_priority',
    'user_roles',
    'status',
    'list',
    'user',
    'tracker',
    'confirmation_status',
    'confirmation_projects',
    'confirmation_trackers',
    'confirmation_unapprovers',
    'confirmation_approvers',
    'confirmation_authors',
    'confirmation_editors',
    'parent',
    'issue_status',
    'base_plan',
  ]);
  const filtersTypeParamsAutocomplete = useRef([
    'issue',
    'issue.issue_id',
    'issue_watchers',
    'confirmation',
    'confirmation_issues',
    'issue.parent_issue',
  ]);
  const filtersTypeParamsMultiple = useRef(['confirmation_issues']);
  const FilterQueries = {
    PROJECT: 'project',
    ISSUE: 'issue',
    TRACKER: 'tracker',
    USER: 'user',
    ACTIVITY: 'activity',
    LIST: 'list',
    ISSUE_STATUS: 'issue_status',
    CALCULATION_TYPES: 'calculation_types',
    ISSUE_WATCHERS: 'issue_watchers',
    USER_GROUPS: 'user_groups',
    ISSUE_PRIORITY: 'issue_priority',
    USER_ROLES: 'user_roles',
    STATUS: 'status',
    CONFIRMATION: 'confirmation',
    CONFIRMATION_STATUS: 'confirmation_status',
    CONFIRMATION_TRACKERS: 'confirmation_trackers',
    CONFIRMATION_ISSUES: 'confirmation_issues',
    CONFIRMATION_APPROVERS: 'confirmation_approvers',
    CONFIRMATION_UNAPPROVERS: 'confirmation_approvers',
    CONFIRMATION_PROJECTS: 'confirmation_projects',
    CONFIRMATION_AUTHORS: 'confirmation_authors',
    CONFIRMATION_EDITORS: 'confirmation_editors',
    ISSUE_PARENT_ISSUE: 'issue.parent_issue',
    PARENT: 'parent',
    ISSUE_ISSUE_ID: 'issue.issue_id',
    BASE_PLAN: 'base_plan',
  };
  const filterQuery = useRef(
    new Map([
      [FilterQueries.PROJECT, { getResponse: getQueryProjects }],
      [FilterQueries.ISSUE, { getResponse: getIssueQuery }],
      [FilterQueries.TRACKER, { getResponse: getQueryIssueTrackers }],
      [FilterQueries.USER, { getResponse: getQueryUsers }],
      [FilterQueries.ACTIVITY, { getResponse: getQueryActivity }],
      [FilterQueries.LIST, { getResponse: getFilterListItems }],
      [
        FilterQueries.CALCULATION_TYPES,
        { getResponse: getFilterCalculationTypes },
      ],
      [FilterQueries.ISSUE_STATUS, { getResponse: getFilterIssueStatuses }],
      [FilterQueries.ISSUE_WATCHERS, { getResponse: getFilterIssueWatcher }],
      [FilterQueries.USER_GROUPS, { getResponse: getFilterIssueUserGroups }],
      [FilterQueries.ISSUE_PRIORITY, { getResponse: getFilterIssuePriorities }],
      [FilterQueries.USER_ROLES, { getResponse: getFilterIssueRoles }],
      [FilterQueries.STATUS, { getResponse: getFilterProjectStatuses }],
      [
        FilterQueries.CONFIRMATION_STATUS,
        { getResponse: getQueryConfirmationStatuses },
      ],
      [
        FilterQueries.CONFIRMATION_TRACKERS,
        { getResponse: getQueryConfirmationTrackers },
      ],
      [
        FilterQueries.CONFIRMATION_ISSUES,
        { getResponse: getQueryConfirmationIssues },
      ],
      [
        FilterQueries.CONFIRMATION,
        { getResponse: getQueryConfirmationConfirmations },
      ],
      [
        FilterQueries.CONFIRMATION_APPROVERS,
        { getResponse: getQueryConfirmationApprovers },
      ],
      [
        FilterQueries.CONFIRMATION_UNAPPROVERS,
        { getResponse: getQueryConfirmationApprovers },
      ],
      [
        FilterQueries.CONFIRMATION_PROJECTS,
        { getResponse: getQueryConfirmationProjects },
      ],
      [
        FilterQueries.CONFIRMATION_AUTHORS,
        { getResponse: getQueryConfirmationAuthors },
      ],
      [
        FilterQueries.CONFIRMATION_EDITORS,
        { getResponse: getQueryConfirmationEditors },
      ],
      [FilterQueries.ISSUE_PARENT_ISSUE, { getResponse: getQueryIssuesParams }],
      [FilterQueries.PARENT, { getResponse: getFilterProjectParents }],
      [FilterQueries.ISSUE_ISSUE_ID, { getResponse: getQueryIssues }],
      [FilterQueries.BASE_PLAN, { getResponse: getFilterBasePlans }],
    ]),
  );
  const urlParams = useRef(new URLSearchParams(location.search));
  const [language, setLanguage] = useState('ru');

  useEffect(() => {
    window.localStorage.getItem('lang') &&
      setLanguage(LanguageMap.get(window.localStorage.getItem('lang')).lang);
  }, []);
  useEffect(() => {
    let filtersAddOptions = data.map(item => {
      let filter = { ...item };
      filterQuery.current.has(item.fieldType) &&
        !filter.options &&
        (filter.options = []);
      !filter.operation &&
        renderOptions(item.fieldType).length > 0 &&
        (filter.operation = renderOptions(item.fieldType)[0]);
      return filter;
    });
    if (filtersAddOptions) {
      setFilters(filtersAddOptions);
    }
  }, [data]);
  const handleClose = filter => {
    let currentFilter = filter;
    if (filter.operation === 'btw') {
      if (
        filter.fieldType === 'date' &&
        currentFilter.values &&
        currentFilter.values.some(item => item === null)
      ) {
        delete currentFilter.values;
      } else {
        if (
          currentFilter.values &&
          currentFilter.values.filter(item => item !== '').length < 2
        ) {
          delete currentFilter.values;
        }
      }
    }
    handleCloseFilter(currentFilter);
    setOpenedFilter('');
    setAnchorEl(null);
  };

  const handleOpen = useCallback(
    async (event, index, filter) => {
      setOpenedFilter(index);
      setAnchorEl(event.currentTarget);
      let updatedObject = {};
      const pos = filters.map(e => e.fieldId).indexOf(filter.fieldId);
      if (filterQuery.current.has(filter.fieldType) && filter.options) {
        !filtersTypeParamsAutocomplete.current.includes(filter.fieldType) &&
          setSearchLoading(true);
        let params = {};
        if (filtersTypeParamsAutocomplete.current.includes(filter.fieldType)) {
          if (
            !filter.values ||
            filter.values.length === 0 ||
            disableFilterOperations.current.includes(filter.values)
          ) {
            return;
          } else {
            const value = Array.isArray(filter.values)
              ? filter.values[0]
              : filter.values;
            !disableFilterOperations.current.includes(value) &&
              (params.search = value);
            !disableFilterOperations.current.includes(value) &&
              !updatedObject.searchValue &&
              (updatedObject.searchValue = value);
          }
        }
        const fieldPapams = ['list', 'user'];
        fieldPapams.includes(filter.fieldType) &&
          (params.field = filter.fieldId);
        pageParams.projectId && (params.project = pageParams.projectId);
        try {
          const { data: response } = await filterQuery.current
            .get(filter.fieldType)
            .getResponse(params);
          if (response) {
            updatedObject = {
              ...filters[pos],
              ...updatedObject,
              options: response,
            };
            const newFields = [
              ...filters.slice(0, pos),
              updatedObject,
              ...filters.slice(pos + 1),
            ];
            setFilters(newFields);
            setSearchLoading(false);
          }
        } catch (error) {
          console.error('ERROR WITH GET', error);
        }
      }
    },
    [filters, pageParams.projectId],
  );

  const handleOperationTypeSelect = useCallback(
    (event, filter) => {
      let updatedObject;
      const pos = filters.map(e => e.fieldId).indexOf(filter.fieldId);
      updatedObject = {
        ...filters[pos],
        operation: event.target.value,
      };
      ['null', 'all'].includes(filter.values) && delete updatedObject.values;
      if (filter.fieldType === 'date') {
        updatedObject = {
          ...filters[pos],
          operation: event.target.value,
        };
        if (!filterDateValues.has(event.target.value.toLowerCase())) {
          updatedObject.values = null;
        } else {
          updatedObject.values = filterDateValues.get(
            event.target.value.toLowerCase(),
          ).value;
        }
      }
      if (event.target.value === 'null') {
        updatedObject.values = 'null';
      }
      if (event.target.value === 'all') {
        updatedObject.values = 'all';
        //delete updatedObject.values;
      }
      if (event.target.value === 'btw' && filter.fieldType !== 'date') {
        updatedObject.values = [];
      }
      const newFields = [
        ...filters.slice(0, pos),
        updatedObject,
        ...filters.slice(pos + 1),
      ];
      setFilters(newFields);
    },
    [filters],
  );

  const handleFilter = useCallback(
    (data, filter) => {
      let updatedObject;
      const pos = filters.map(e => e.fieldId).indexOf(filter.fieldId);
      updatedObject = {
        ...filters[pos],
        values: data,
      };
      data === '' && delete updatedObject.values;
      if (data && data.constructor === Object && data.valueId) {
        updatedObject = {
          ...filters[pos],
          values: data.valueId,
        };
      }
      if (Array.isArray(data) && filter.fieldType !== 'date') {
        let values;
        if (filter.operation === 'btw') {
          values = data;
        } else {
          const options = filter.options.map(item => item.valueId);
          values = data
            .filter(item =>
              options.includes(item.valueId ? item.valueId : item),
            )
            .map(item => (item.valueId ? item.valueId : item));
        }
        updatedObject = {
          ...filters[pos],
          values: values,
        };
        data.length === 0 && delete updatedObject.values;
      }
      if (filter.fieldType === 'date') {
        if (data) {
          updatedObject = {
            ...filters[pos],
            values: data,
          };
        } else {
          updatedObject = {
            ...filters[pos],
            values: null,
          };
        }
        //Array.isArray(data) && data.some(item => item === null) && delete updatedObject.values;
        !data && delete updatedObject.values;
      }
      if (filtersTypeBoolean.current.includes(filter.fieldType)) {
        updatedObject = {
          ...filters[pos],
          values: data.target.value,
        };
        data === '' && delete updatedObject.values;
      }
      const newFilters = [
        ...filters.slice(0, pos),
        updatedObject,
        ...filters.slice(pos + 1),
      ];
      setFilters(newFilters);
    },
    [filters, filtersTypeBoolean],
  );

  const changeAutocomleteTextfield = useCallback(
    async (value, filter, number = false) => {
      if (number) {
        const reg = new RegExp(/^[0-9,.]*$/);
        if (!reg.test(value)) {
          return;
        }
      }
      let params = {
        search: value,
      };
      filter.fieldId === FilterQueries.ISSUE_PARENT_ISSUE &&
        urlParams.current.get('project') &&
        (params.project = urlParams.current.get('project'));
      const pos = filters.map(e => e.fieldId).indexOf(filter.fieldId);
      let updatedObject;
      if (value !== '') {
        try {
          setSearchLoading(true);
          const { data: response } = await filterQuery.current
            .get(filter.fieldType)
            .getResponse(params);
          if (response) {
            updatedObject = {
              ...filters[pos],
              searchValue: value,
              options: response,
            };
            setSearchLoading(false);
          }
        } catch (error) {
          console.error('ERROR WITH EDIT', error);
        }
      } else {
        updatedObject = {
          ...filters[pos],
          searchValue: value,
          options: [],
        };
      }
      const newFilters = [
        ...filters.slice(0, pos),
        updatedObject,
        ...filters.slice(pos + 1),
      ];
      setFilters(newFilters);
    },
    [FilterQueries.ISSUE_PARENT_ISSUE, filters],
  );

  const handleNumberField = useCallback(
    (value, field, type = '') => {
      const reg = new RegExp(/^[0-9,.]*$/);
      if (reg.test(value)) {
        if (field.operation === 'btw') {
          let formattedValue = field.values ? field.values : [];
          type === 'from' && (formattedValue[0] = value);
          type === 'to' && (formattedValue[1] = value);
          handleFilter(formattedValue, field);
        } else {
          handleFilter(value, field);
        }
      }
    },
    [handleFilter],
  );

  return (
    <Grid container spacing={3}>
      {filters &&
        filters
          .sort((a, b) => a.position - b.position)
          .map((filter, index) => {
            return (
              <Grid key={index}>
                <StyledFilterPopoverButton
                  activeStyle={filter.values && filter.values.length > 0}
                  aria-describedby={'filter' + index}
                  variant='contained'
                  onClick={event => handleOpen(event, index, filter)}
                >
                  <Typography
                    variant='h4'
                    sx={{
                      color: '#7174AC',
                    }}
                  >
                    {filter.fieldName}
                  </Typography>
                  {filter.fieldType === 'base_plan' && (
                    <ChevronIcon
                      direction='down'
                      viewBox='0 0 24 24'
                      width={24}
                      height={24}
                    />
                  )}
                </StyledFilterPopoverButton>
                <StyledFilterPopover
                  id={'filter' + index}
                  open={openedFilter === index}
                  anchorEl={anchorEl}
                  onClose={() => handleClose(filter)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                >
                  <Grid
                    container
                    sx={{
                      flexDirection: 'column',
                    }}
                  >
                    {filter.fieldType !== 'base_plan' && (
                      <Grid>
                        <Grid
                          container
                          sx={{
                            padding: '6px 8px 6px 16px',
                            borderBottom: '1px solid #E4E4EF',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'nowrap',
                          }}
                        >
                          <Grid
                            sx={{
                              paddingRight: '10px',
                            }}
                          >
                            <Typography
                              variant='h5'
                              sx={{
                                fontWeight: 700,
                              }}
                            >
                              {filter.fieldName}
                            </Typography>
                          </Grid>
                          <FormControl sx={{ flexShrink: 0 }}>
                            <StyledSelectLabel id='select-label'>
                              {getLocalString('labels', 'select')}
                            </StyledSelectLabel>
                            <StyledFilterPopoverSelect
                              IconComponent={props => (
                                <ChevronIcon direction={'down'} {...props} />
                              )}
                              value={filter.operation ? filter.operation : ''}
                              onChange={event =>
                                handleOperationTypeSelect(event, filter)
                              }
                              MenuProps={{
                                slots: { paper: StyledFilterPopoverSelectMenu },
                              }}
                            >
                              {renderOptions(filter.fieldType).map(
                                (item, index) => {
                                  return (
                                    <MenuItem value={item} key={index}>
                                      <Typography
                                        variant='h5'
                                        sx={{
                                          color: '#212346',
                                        }}
                                      >
                                        {getLocalString('labels', item)}
                                      </Typography>
                                    </MenuItem>
                                  );
                                },
                              )}
                            </StyledFilterPopoverSelect>
                          </FormControl>
                        </Grid>
                      </Grid>
                    )}
                    <Grid>
                      {filtersTypeText.current.includes(filter.fieldType) && (
                        <StyledFilterPopoverTextfield
                          disabled={disableFilterOperations.current.includes(
                            filter.operation,
                          )}
                          variant='outlined'
                          placeholder={getLocalString('labels', 'input')}
                          onChange={event =>
                            handleFilter(event.target.value, filter)
                          }
                          value={
                            filter.values &&
                            !disableFilterOperations.current.includes(
                              filter.values,
                            )
                              ? filter.values
                              : ''
                          }
                        ></StyledFilterPopoverTextfield>
                      )}
                      {filtersTypeNumber.current.includes(filter.fieldType) &&
                        (filter.operation === 'btw' ? (
                          <Grid
                            container
                            sx={{
                              alignItems: 'center',
                            }}
                          >
                            <StyledFilterPopoverTextfield
                              sx={{ flex: 1 }}
                              disabled={disableFilterOperations.current.includes(
                                filter.operation,
                              )}
                              variant='outlined'
                              placeholder={getLocalString('labels', 'input')}
                              onChange={event =>
                                handleNumberField(
                                  event.target.value,
                                  filter,
                                  'from',
                                )
                              }
                              value={
                                filter.values &&
                                filter.values[0] &&
                                !disableFilterOperations.current.includes(
                                  filter.values,
                                )
                                  ? filter.values[0]
                                  : ''
                              }
                            ></StyledFilterPopoverTextfield>
                            <Box component={'span'}>-</Box>
                            <StyledFilterPopoverTextfield
                              sx={{ flex: 1 }}
                              disabled={disableFilterOperations.current.includes(
                                filter.operation,
                              )}
                              variant='outlined'
                              placeholder={getLocalString('labels', 'input')}
                              onChange={event =>
                                handleNumberField(
                                  event.target.value,
                                  filter,
                                  'to',
                                )
                              }
                              value={
                                filter.values &&
                                filter.values[1] &&
                                !disableFilterOperations.current.includes(
                                  filter.values,
                                )
                                  ? filter.values[1]
                                  : ''
                              }
                            ></StyledFilterPopoverTextfield>
                          </Grid>
                        ) : (
                          <StyledFilterPopoverTextfield
                            disabled={disableFilterOperations.current.includes(
                              filter.operation,
                            )}
                            variant='outlined'
                            placeholder={getLocalString('labels', 'input')}
                            onChange={event =>
                              handleNumberField(event.target.value, filter)
                            }
                            value={
                              filter.values &&
                              !disableFilterOperations.current.includes(
                                filter.values,
                              )
                                ? filter.values
                                : ''
                            }
                          ></StyledFilterPopoverTextfield>
                        ))}
                      {filtersTypeAutocomplete.current.includes(
                        filter.fieldType,
                      ) && (
                        <StyledFilterPopoverAutocomplete
                          noOptionsText={getLocalString('labels', 'notFound')}
                          disabled={disableFilterOperations.current.includes(
                            filter.operation,
                          )}
                          onChange={(event, value) =>
                            handleFilter(value, filter)
                          }
                          open={true}
                          multiple
                          loading={searchLoading}
                          disablePortal
                          options={sortOptions(filter.options, filter)}
                          value={
                            filter.values &&
                            !disableFilterOperations.current.includes(
                              filter.values,
                            )
                              ? filter.values
                              : []
                          }
                          isOptionEqualToValue={(option, value) => {
                            const currentValue = value.valueId
                              ? value.valueId
                              : value.toString();
                            return option.valueId === currentValue;
                          }}
                          clearIcon={<ClearIcon></ClearIcon>}
                          disableClearable
                          renderOption={(props, option, { selected }) => {
                            return (
                              <li
                                {...props}
                                key={option.valueId}
                                aria-disabled={disableFilterOperations.current.includes(
                                  filter.operation,
                                )}
                              >
                                <Checkbox
                                  icon=<CheckboxIcon
                                    width='12'
                                    height='12'
                                    viewBox='0 0 12 12'
                                  />
                                  checkedIcon=<CheckboxIconChecked
                                    width='12'
                                    height='12'
                                    viewBox='0 0 12 12'
                                  />
                                  style={{ marginRight: 8 }}
                                  checked={selected}
                                />
                                {option.valueName}
                              </li>
                            );
                          }}
                          getOptionLabel={option => option.valueName || ''}
                          renderInput={params => (
                            <TextField
                              {...params}
                              placeholder={getLocalString('labels', 'input')}
                              slotProps={{
                                input: {
                                  ...params.InputProps,
                                  endAdornment: searchLoading ? (
                                    <CircularProgress
                                      color='inherit'
                                      size={20}
                                    />
                                  ) : null,
                                },
                              }}
                            />
                          )}
                          slots={{
                            popper: props => (
                              <Popper
                                {...props}
                                placement='bottom-start'
                              ></Popper>
                            ),
                          }}
                        />
                      )}
                      {filtersTypeParamsAutocomplete.current.includes(
                        filter.fieldType,
                      ) && (
                        <StyledFilterPopoverAutocomplete
                          disabled={disableFilterOperations.current.includes(
                            filter.operation,
                          )}
                          onChange={(event, value) =>
                            handleFilter(value, filter)
                          }
                          open={true}
                          multiple
                          loading={searchLoading}
                          disablePortal
                          disableClearable
                          options={sortOptions(filter.options, filter)}
                          filterOptions={x => x}
                          value={
                            filter.values &&
                            !disableFilterOperations.current.includes(
                              filter.values,
                            )
                              ? filter.values
                              : []
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.valueId === value
                          }
                          clearIcon={<ClearIcon></ClearIcon>}
                          renderOption={(props, option) => {
                            let optionChecked = false;
                            if (filter.values) {
                              option.valueId === filter.values &&
                                (optionChecked = true);
                              Array.isArray(filter.values) &&
                                filter.values.includes(option.valueId) &&
                                (optionChecked = true);
                            }
                            return (
                              <li
                                {...props}
                                key={option.valueId}
                                aria-disabled={disableFilterOperations.current.includes(
                                  filter.operation,
                                )}
                              >
                                <Checkbox
                                  icon=<CheckboxIcon
                                    width='12'
                                    height='12'
                                    viewBox='0 0 12 12'
                                  />
                                  checkedIcon=<CheckboxIconChecked
                                    width='12'
                                    height='12'
                                    viewBox='0 0 12 12'
                                  />
                                  style={{ marginRight: 8 }}
                                  checked={optionChecked}
                                />
                                {option.valueName}
                              </li>
                            );
                          }}
                          getOptionLabel={option => option.valueName || ''}
                          onInputChange={(event, newInputValue, reason) =>
                            reason === 'input' &&
                            changeAutocomleteTextfield(
                              newInputValue,
                              filter,
                              filtersNumbers.current.includes(filter.fieldType),
                            )
                          }
                          inputValue={
                            filter.searchValue &&
                            !disableFilterOperations.current.includes(
                              filter.values,
                            )
                              ? filter.searchValue
                              : ''
                          }
                          renderInput={params => (
                            <TextField
                              {...params}
                              placeholder={getLocalString('labels', 'input')}
                              slotProps={{
                                input: {
                                  ...params.InputProps,
                                  endAdornment: (
                                    <React.Fragment>
                                      {searchLoading ? (
                                        <CircularProgress
                                          color='inherit'
                                          size={20}
                                        />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </React.Fragment>
                                  ),
                                },
                              }}
                            />
                          )}
                          slots={{
                            popper: props => (
                              <Popper
                                {...props}
                                placement='bottom-start'
                              ></Popper>
                            ),
                          }}
                        />
                      )}

                      {filter.fieldType === 'date' && (
                        <LocalizationProvider
                          dateAdapter={AdapterDayjs}
                          adapterLocale={language}
                        >
                          {filtersDateRange.includes(filter.operation) ? (
                            <StyledRangeDatePicker
                              disabled={disableFilterOperations.current.includes(
                                filter.operation,
                              )}
                              label={''}
                              calendars={1}
                              value={
                                filter.values &&
                                !disableFilterOperations.current.includes(
                                  filter.values,
                                )
                                  ? filter.values
                                  : null
                              }
                              onChange={value => handleFilter(value, filter)}
                              //renderInput={(startProps, endProps) => (
                              //  <>
                              //    <TextField {...startProps} />
                              //    <TextField {...endProps} />
                              //  </>
                              //)}
                              format={'DD.MM.YYYY'}
                              slots={{
                                openPickerIcon: ({ ...props }) => (
                                  <CalendarFormIcon {...props} />
                                ),
                                leftArrowIcon: () => (
                                  <ChevronIcon
                                    direction='left'
                                    viewBox='0 0 24 24'
                                    width={24}
                                    height={24}
                                  />
                                ),
                                rightArrowIcon: () => (
                                  <ChevronIcon
                                    direction='right'
                                    viewBox='0 0 24 24'
                                    width={24}
                                    height={24}
                                  />
                                ),
                                rightArrowButton: componentProps => (
                                  <IconButton disableRipple {...componentProps}>
                                    {componentProps.children}
                                  </IconButton>
                                ),
                                leftArrowButton: componentProps => (
                                  <IconButton disableRipple {...componentProps}>
                                    {componentProps.children}
                                  </IconButton>
                                ),
                                day: DateRangePickerDay,
                              }}
                            />
                          ) : (
                            <StyledDatePicker
                              disabled={disableFilterOperations.current.includes(
                                filter.operation,
                              )}
                              format={'DD.MM.YYYY'}
                              slots={{
                                openPickerIcon: ({ ...props }) => (
                                  <CalendarFormIcon {...props} />
                                ),
                                leftArrowIcon: () => (
                                  <ChevronIcon
                                    direction='left'
                                    viewBox='0 0 24 24'
                                    width={24}
                                    height={24}
                                  />
                                ),
                                rightArrowIcon: () => (
                                  <ChevronIcon
                                    direction='right'
                                    viewBox='0 0 24 24'
                                    width={24}
                                    height={24}
                                  />
                                ),
                                rightArrowButton: componentProps => (
                                  <IconButton disableRipple {...componentProps}>
                                    {componentProps.children}
                                  </IconButton>
                                ),
                                leftArrowButton: componentProps => (
                                  <IconButton disableRipple {...componentProps}>
                                    {componentProps.children}
                                  </IconButton>
                                ),
                              }}
                              value={
                                filter.values &&
                                !disableFilterOperations.current.includes(
                                  filter.values,
                                )
                                  ? filter.values
                                  : null
                              }
                              onChange={newValue =>
                                handleFilter(newValue, filter)
                              }
                            />
                          )}
                        </LocalizationProvider>
                      )}
                      {filtersTypeBoolean.current.includes(
                        filter.fieldType,
                      ) && (
                        <RadioGroup
                          disabled={disableFilterOperations.current.includes(
                            filter.operation,
                          )}
                          onChange={value => handleFilter(value, filter)}
                          value={filter.values ? filter.values[0] : ''}
                        >
                          <StyledBooleanLabel
                            value={true}
                            checked={
                              filter.values &&
                              filter.values !== '' &&
                              filter.values === 'true'
                            }
                            control={
                              <Radio
                                disableRipple
                                icon={
                                  <CheckboxIcon
                                    width='12'
                                    height='12'
                                    viewBox='0 0 12 12'
                                  />
                                }
                                checkedIcon={
                                  <CheckboxIconChecked
                                    width='12'
                                    height='12'
                                    viewBox='0 0 12 12'
                                  />
                                }
                              />
                            }
                            label={getLocalString('labels', 'yes')}
                          />
                          <StyledBooleanLabel
                            value={false}
                            checked={
                              filter.values &&
                              filter.values !== '' &&
                              filter.values === 'false'
                            }
                            control={
                              <Radio
                                disableRipple
                                icon={
                                  <CheckboxIcon
                                    width='12'
                                    height='12'
                                    viewBox='0 0 12 12'
                                  />
                                }
                                checkedIcon={
                                  <CheckboxIconChecked
                                    width='12'
                                    height='12'
                                    viewBox='0 0 12 12'
                                  />
                                }
                              />
                            }
                            label={getLocalString('labels', 'no')}
                          />
                        </RadioGroup>
                      )}
                    </Grid>
                  </Grid>
                </StyledFilterPopover>
              </Grid>
            );
          })}
    </Grid>
  );
};

export default GridFiltersPanelNew;
